const STORAGE_VERSION = 'v1';
const MAGIC_LINK_REDIRECT_PATH_KEY = `natter:auth:magicLinkRedirectPath:${STORAGE_VERSION}`;
const MAGIC_LINK_PREVIOUS_PATH_KEY = `natter:auth:magicLinkPreviousPath:${STORAGE_VERSION}`;
const MAGIC_LINK_START_TIME_KEY = `natter:auth:magicLinkStartTime:${STORAGE_VERSION}`;
const MAGIC_LINK_COOLDOWN_KEY = `natter:auth:magicLinkCooldown:${STORAGE_VERSION}`;

/**
 * Stores the redirect path in sessionStorage for TellUsAboutYourself page.
 * Used to persist the redirect path across page refresh so the extended form is shown.
 */
export const setMagicLinkRedirectPath = (path: string): void => {
  sessionStorage.setItem(MAGIC_LINK_REDIRECT_PATH_KEY, path);
};

/**
 * Retrieves the stored redirect path from sessionStorage.
 */
export const getMagicLinkRedirectPath = (): string | null => sessionStorage.getItem(MAGIC_LINK_REDIRECT_PATH_KEY);

/**
 * Clears the redirect path from sessionStorage.
 * Should be called after successful profile submission.
 */
export const clearMagicLinkRedirectPath = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_REDIRECT_PATH_KEY);
};

/**
 * Stores the previous path in sessionStorage for magic link flow.
 * Used to redirect users back to where they came from after authentication.
 */
export const setMagicLinkPreviousPath = (path: string): void => {
  sessionStorage.setItem(MAGIC_LINK_PREVIOUS_PATH_KEY, path);
};

/**
 * Retrieves the stored previous path from sessionStorage.
 */
export const getMagicLinkPreviousPath = (): string | null => sessionStorage.getItem(MAGIC_LINK_PREVIOUS_PATH_KEY);

/**
 * Clears the previous path from sessionStorage.
 */
export const clearMagicLinkPreviousPath = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_PREVIOUS_PATH_KEY);
};

/**
 * Stores the start time of magic link flow in sessionStorage.
 * Used to calculate time-to-login metric.
 */
export const setMagicLinkStartTime = (): void => {
  sessionStorage.setItem(MAGIC_LINK_START_TIME_KEY, Date.now().toString());
};

/**
 * Retrieves the stored magic link start time from sessionStorage.
 */
export const getMagicLinkStartTime = (): number | null => {
  const startTime = sessionStorage.getItem(MAGIC_LINK_START_TIME_KEY);
  return startTime ? parseInt(startTime, 10) : null;
};

/**
 * Clears the magic link start time from sessionStorage.
 * Should be called after successful authentication.
 */
export const clearMagicLinkStartTime = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_START_TIME_KEY);
};

/**
 * Stores the cooldown end timestamp and associated email in sessionStorage.
 * Used to persist the resend cooldown timer across back-navigation.
 */
export const setMagicLinkCooldown = (email: string, endTime: number): void => {
  sessionStorage.setItem(MAGIC_LINK_COOLDOWN_KEY, JSON.stringify({ email, endTime }));
};

/**
 * Retrieves the remaining cooldown seconds for the given email.
 * @returns Remaining seconds if cooldown is active for this email, null otherwise.
 */
export const getMagicLinkCooldownRemaining = (email: string): number | null => {
  const raw = sessionStorage.getItem(MAGIC_LINK_COOLDOWN_KEY);
  if (!raw) return null;

  try {
    const { email: storedEmail, endTime } = JSON.parse(raw);
    if (storedEmail !== email) return null;

    const remaining = Math.ceil((endTime - Date.now()) / 1000);
    return remaining > 0 ? remaining : null;
  } catch {
    return null;
  }
};

/**
 * Clears the magic link cooldown data from sessionStorage.
 */
export const clearMagicLinkCooldown = (): void => {
  sessionStorage.removeItem(MAGIC_LINK_COOLDOWN_KEY);
};
